<template>
  <v-card
    v-if="calendar.length"
    width="100%"
    id="activityCalendar"
    flat
    color="transparent"
  >
    <v-card-title class="px-0 py-2">
      <h2 class="black--text homepageTitle">{{ title }}</h2>
      <v-btn
      color="primary"
      class="primary-font-color ml-4"
      height="30px"
      @click="navigateToFullCalendar">
        <v-icon>mdi-calendar</v-icon>
        {{$translate('FULL_CALENDAR')}}
      </v-btn>
    </v-card-title>

    <v-card-text style="overflow-x: auto" class="px-0">
      <ItemGroup :items="calendar">
        <template v-slot:item="{ item }">
          <v-hover v-slot="{ hover }">
            <v-card
              color="grey lighten-5"
              :elevation="hover ? 5 : 1"
              @click="viewEvent(item)"
            >
              <!-- Picture -->
              <v-img :src="item.PictureUrl" :aspect-ratio="1.618 / 1">
                <!-- Date -->
                <v-overlay color="primary" absolute class="activity-overlay">
                  <v-col class="py-2">
                    <div class="text-caption text-center activity-overlay_text">
                      {{ getMonth(item.StartDate) }}
                    </div>
                    <div
                      class="text-h5 text-center font-weight-bold activity-overlay_text"
                    >
                      {{ getDay(item.StartDate) }}
                    </div>
                  </v-col>
                </v-overlay>
              </v-img>

              <!-- Event type -->
              <!-- <v-card-subtitle class="pb-0 pt-2 px-2">
                {{ item.Type }}
              </v-card-subtitle> -->

              <!-- Name -->
              <v-card-title class="pt-2 px-2">
                <div
                  class="text-truncate pt-0 itemTitleFontSize font-weight-bold"
                >
                  {{ item.Name }}
                </div>
              </v-card-title>

              <!-- Description -->
              <v-card-subtitle
                class="px-2 text-truncate"
                v-html="item.Subtitle"
              ></v-card-subtitle>

              <!-- Time -->
              <v-card-text class="px-2">
                <v-chip outlined small>
                  <v-icon left small>mdi-clock</v-icon>
                  {{ item.StartTime }} - {{ item.EndTime }}
                </v-chip>
              </v-card-text>
            </v-card>
          </v-hover>
        </template>
      </ItemGroup>
    </v-card-text>
  </v-card>
</template>

<script>
import utility from '@/shared/utility';
import doRequest, { addLog } from '@/api/dssRequest';
import ItemGroup from '@/components/generalComponents/uiComponents/ItemGroup';

export default {
  name: 'ActivityCalendar',
  components: {
    ItemGroup,
  },
  data() {
    return {
      calendar: [],
      maxHeightOfCalendar: 1500,
    };
  },
  computed: {
    userObjId() {
      return this.$store.getters['auth/userObjectId'];
    },
    pgDetails() {
      return this.$store.getters['Account/partnerGroupDetails'];
    },
    primaryColor() {
      if (this.pgDetails == null || this.pgDetails.PrimaryColor == null) {
        return '';
      }

      return this.pgDetails.PrimaryColor;
    },
    secondaryColor() {
      if (this.pgDetails == null || this.pgDetails.SecondaryColor == null) {
        return '';
      }

      return this.pgDetails.SecondaryColor;
    },
    title() {
      if (
        this.pgDetails != null &&
        this.pgDetails.CalendarTitle != null &&
        this.pgDetails.CalendarTitle.trim() !== ''
      ) {
        return this.pgDetails.CalendarTitle.trim();
      }
      return this.$translate('AGENDA');
    },
  },
  mounted() {
    this.getCalendar();
  },
  methods: {
    getCalendar() {
      // For upcoming month
      const startDateYMD = utility.getCurrentDateYMD();
      const endDate = utility.addDays(startDateYMD, 30 * 6);

      const payload = {
        StartDate: utility.getCurrentDateDMY(),
        EndDate: endDate,
        FullCalendar: false,
      };

      this.calendar = [];

      doRequest(
        (response) => {
          if (response.data.Calendar != null) {
            const sortedCalendar = this.sortCalendar(response.data.Calendar);
            this.calendar = sortedCalendar;
          }
        },
        () => {},
        'getCalendar',
        this.userObjId,
        payload,
        'GET'
      );
    },
    sortCalendar(rawCalendar) {
      // Sorted calendar contains a list of activities ordered by date time
      const sortedCalendar = [];

      Object.entries(rawCalendar).forEach(([dateStr, eventObjs]) => {
        Object.entries(eventObjs).forEach(([eventId, eventObj]) => {
          const dateYMDStr = utility.dateFromDMYToYMD(dateStr);
          const dateTimeStr = `${dateYMDStr} ${eventObj.StartTime}`;
          const dateObj = new Date(dateTimeStr);

          const activity = { ...eventObj };
          activity.Date = dateObj;
          activity.StartDate = dateStr;

          sortedCalendar.push(activity);
        });
      });

      sortedCalendar.sort((a, b) => a.Date.getTime() - b.Date.getTime());
      return sortedCalendar;
    },
    getDateArray(dateDMYStr) {
      if (dateDMYStr != null && dateDMYStr !== '') {
        const dateYMDStr = utility.dateFromDMYToYMD(dateDMYStr);
        const dateObj = new Date(dateYMDStr);
        const dateStr = dateObj.toDateString();
        // Format: Tue Aug 19 1975
        return dateStr.split(' ');
      }

      return [];
    },
    getMonth(dateDMYStr) {
      const dateArray = this.getDateArray(dateDMYStr);

      if (dateArray.length >= 2) {
        return dateArray[1];
      }

      return '';
    },
    getDay(dateDMYStr) {
      const dateArray = this.getDateArray(dateDMYStr);

      if (dateArray.length >= 3) {
        return dateArray[2];
      }

      return '';
    },
    getDateStr(dateDMYStr) {
      const dateArray = this.getDateArray(dateDMYStr);
      const [weekDay, month, day, year] = dateArray;

      return `${weekDay} ${month} ${day} ${year}`;
    },
    viewEvent(event) {
      // Add user interaction to log
      addLog(
        this.$store.getters['auth/userName'],
        event.EventId,
        'Event',
        'Home',
        -1
      );

      this.$router.push({
        name: 'EventDetails',
        params: { event_id: event.EventId },
      });
    },
    navigateToFullCalendar() {
      this.$router.push({ name: 'Calendar' }); 
    },
    getMaxHeight() {
      const agendaEl = document.getElementById('agendaItemsContainer');
      const agendaElTop = agendaEl.offsetTop;

      const refEl = document.getElementById('mainContentsContainer');
      const refElTop = refEl.offsetTop;
      const refElHeight = refEl.offsetHeight;
      const refElBottom = refElTop + refElHeight;

      this.maxHeightOfCalendar = refElBottom - agendaElTop;
    },
  },
};
</script>

<style scoped lang="scss">
.activity-overlay {
  right: 60%;
  bottom: 50%;
}
.activity-overlay_text {
  text-shadow: 1px 1px 3px #000000;
}
</style>
